import environment from "flow/configuration/environment";
import MyFlow from "flow/MyFlow";
import config from "flow/config";
import { Session } from "flow/session/session";
import activator from "flow/utils/activator";
import localStorageChannel from "flow/session/localStorageChannel";
import backgroundTimer from "flow/timers/backgroundTimer";
import { InactivityEventQueue } from "flow/session/InactivityEventQueue";

function debug(...params: any[]) {
    if (environment.isEnabled(environment.keys.debugInactivity)) {
        console.debug(...params);
    }
}

const acceptedDifference = 2;
const acceptedResponseTime = 3;

type TimeSyncObj = {
    isOriginal: boolean;
    secondsElapsed: number;
    hasUnsavedData: boolean;
};

export default class InactivityMonitor {
    private secondsElapsed: number = 0;
    private timerId: number;
    private inactivityTimeoutInSeconds = (environment.get(environment.keys.inactivityTimeout) as number) * 60;
    private inactivityDialogTimeoutInSeconds =
        (environment.get(environment.keys.inactivityDialogTimeout) as number) * 60;
    private inactivityDialogTimeoutUnsavedInSeconds =
        (environment.get(environment.keys.inactivityDialogTimeoutUnsaved) as number) * 60;
    private isShowingDialog = false;
    private thereIsUnsavedData = false;
    private dialogStartedWithTimeoutForUnsavedData = true;
    private eventQueue = new InactivityEventQueue();

    constructor(
        private readonly myFlow: MyFlow,
        private readonly session: Session
    ) {}

    isWorkspaceDirty = () => {
        return !activator.canLeaveSweepWithoutUserFeedback();
    };

    startInactivityTimer = () => {
        if (this.inactivityTimeoutInSeconds === 0) {
            return;
        }

        this.timerId = backgroundTimer.setInterval(() => {
            ++this.secondsElapsed;
            debug(this.secondsElapsed);

            if (this.secondsElapsed >= this.inactivityTimeoutInSeconds) {
                this.launchDialog();
            }
        }, 1000);

        window.addEventListener("click", this.reset, false);
        window.addEventListener("wheel", this.reset, false);
        window.addEventListener("keyup", this.reset, false);
        window.addEventListener("touchend", this.reset, false);
    };

    stopInactivityTimer = () => {
        backgroundTimer.clearInterval(this.timerId);
        this.timerId = 0;

        window.removeEventListener("click", this.reset, false);
        window.removeEventListener("wheel", this.reset, false);
        window.removeEventListener("keyup", this.reset, false);
        window.removeEventListener("touchend", this.reset, false);
    };

    timerRunning = () => {
        return this.timerId !== 0;
    };

    registerActivityFromExternalSource = () => {
        if (this.timerRunning()) {
            this.reset();
        }
    };

    reset = () => {
        this.secondsElapsed = 0;
    };

    launchDialog = () => {
        debug("Launch countdown dialog");
        this.thereIsUnsavedData = false;
        this.dialogStartedWithTimeoutForUnsavedData = false;
        this.eventQueue.clear();
        this.stopInactivityTimer();
        this.requestTabSynchronization();

        backgroundTimer.setTimeout(() => {
            if (this.secondsElapsed < this.inactivityTimeoutInSeconds) {
                // There was a tab with more recent activity
                // Abort for now
                debug("Timer reverted to", this.secondsElapsed);
                this.startInactivityTimer();
                return;
            }

            debug("Dialog launch sync finished");
            if (this.inactivityDialogTimeoutInSeconds > 0) {
                this.isShowingDialog = true;

                let dialogTimeoutInSeconds = this.inactivityDialogTimeoutInSeconds;
                if (
                    this.inactivityDialogTimeoutUnsavedInSeconds > 0 &&
                    (this.isWorkspaceDirty() || this.thereIsUnsavedData)
                ) {
                    this.dialogStartedWithTimeoutForUnsavedData = true;
                    dialogTimeoutInSeconds = this.inactivityDialogTimeoutUnsavedInSeconds;
                }

                debug("Dialog will stay up for", dialogTimeoutInSeconds);

                this.myFlow
                    .showCustomDialog(config.componentIds.logoutCountdownDialog, {
                        timeoutInSeconds: dialogTimeoutInSeconds,
                        eventQueue: this.eventQueue
                    })
                    .then((result: boolean) => {
                        this.isShowingDialog = false;
                        if (result === true) {
                            // User pressed renew
                            debug("User aborted dialog");
                            this.reset();
                            this.requestTabSynchronization();
                            this.startInactivityTimer();
                        } else if (result === false) {
                            // User pressed logout
                            debug("User clicked logout or dialog timed out");
                            this.session.forceLogout(true);
                        } else {
                            debug("Other tab aborted dialog");
                            // Closed because of activity in other tab
                            // We will start timer to continue on current value
                            this.startInactivityTimer();
                        }
                    })
                    .catch(() => {
                        debug("Exception from dialog");
                        this.isShowingDialog = false;
                        // Something went wrong, not sure what to do
                    });
            } else {
                debug("No dialog timeout set, just force logout directly");
                this.session.forceLogout(true);
            }
        }, acceptedResponseTime);
    };

    requestTabSynchronization = () => {
        localStorageChannel.publish(
            config.storageChannelTopics.timeSync,
            JSON.stringify({
                isOriginal: true,
                secondsElapsed: this.secondsElapsed,
                hasUnsavedData: this.isWorkspaceDirty()
            } as TimeSyncObj)
        );
    };

    respondToTabSynchronization = () => {
        localStorageChannel.publish(
            config.storageChannelTopics.timeSync,
            JSON.stringify({
                isOriginal: false,
                secondsElapsed: this.secondsElapsed,
                hasUnsavedData: this.isWorkspaceDirty()
            } as TimeSyncObj)
        );
    };

    subscribeToActivity = () => {
        localStorageChannel.subscribe(config.storageChannelTopics.timeSync, this.onSyncEvent);
    };

    unsubscribeToActivity = () => {
        localStorageChannel.unsubscribe(config.storageChannelTopics.timeSync, this.onSyncEvent);
    };

    private useDifferentTimeoutForUnsavedData = () => {
        return (
            this.inactivityDialogTimeoutUnsavedInSeconds > 0 &&
            this.inactivityDialogTimeoutInSeconds !== this.inactivityDialogTimeoutUnsavedInSeconds
        );
    };

    private onSyncEvent = (message: string) => {
        try {
            const timeSyncObj = JSON.parse(message) as TimeSyncObj;

            const secondsElapsed = timeSyncObj.secondsElapsed;

            if (isNaN(secondsElapsed)) {
                return;
            }

            if (timeSyncObj.isOriginal) {
                debug("Original message received", timeSyncObj);

                const thisTabHasUnsavedData = this.isWorkspaceDirty();
                if (thisTabHasUnsavedData) {
                    debug("We have unsaved data");
                }

                // Reset dirty flag
                this.thereIsUnsavedData = thisTabHasUnsavedData || timeSyncObj.hasUnsavedData;
                if (this.thereIsUnsavedData) {
                    debug("At least someone has unsaved data");
                }

                // Original request, respond if more recently active or if dirty
                if (
                    secondsElapsed > acceptedDifference + this.secondsElapsed ||
                    (!timeSyncObj.hasUnsavedData && thisTabHasUnsavedData)
                ) {
                    debug("I have more information and will respond");
                    this.respondToTabSynchronization();
                }
            } else {
                debug("Response message received", timeSyncObj);

                // Update dirty flag
                this.thereIsUnsavedData = this.thereIsUnsavedData || timeSyncObj.hasUnsavedData;
            }

            if (secondsElapsed + acceptedDifference < this.secondsElapsed) {
                // Someone else more recently active
                debug(`someone was active more recently, back from ${this.secondsElapsed} to ${secondsElapsed}`);
                this.secondsElapsed = secondsElapsed;

                if (this.isShowingDialog) {
                    debug("Pull down dialog");
                    this.eventQueue.queue(config.inactivityTopics.closeInactivityDialog);
                }
            }

            if (this.isShowingDialog && timeSyncObj.hasUnsavedData && !this.dialogStartedWithTimeoutForUnsavedData) {
                // Dialog was able to launch before other tab responded that it was dirty
                debug("Dialog already started");

                if (this.useDifferentTimeoutForUnsavedData()) {
                    debug("Need to adjust timeout", this.inactivityDialogTimeoutUnsavedInSeconds);
                    // We will tell dialog to adjust timeout accordingly
                    this.dialogStartedWithTimeoutForUnsavedData = true;

                    this.eventQueue.queue(
                        config.inactivityTopics.otherTabHasUnsavedData,
                        this.inactivityDialogTimeoutUnsavedInSeconds
                    );
                }
            }
        } catch (e) {
            this.myFlow.error("Malformed timesync object");
            this.myFlow.logObjectsAsErrors(e);
            return;
        }
    };
}
